import React from 'react'
import PropTypes from 'prop-types'
import { Box, Typography } from '@mui/material'
import ListAltIcon from '@mui/icons-material/ListAlt'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import LockIcon from '@mui/icons-material/Lock'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { HOLD_ALLOCATION_BUCKETS, BUCKET_LABELS } from '../../../../../constants/hold-allocation-constants'

const BUCKET_CONFIG = [
  {
    key: HOLD_ALLOCATION_BUCKETS.ALL,
    countKey: 'allOrders',
    icon: <ListAltIcon sx={{ fontSize: 22, color: '#145a4b' }} />,
    accentColor: '#145a4b',
  },
  {
    key: HOLD_ALLOCATION_BUCKETS.RELEASABLE,
    countKey: 'releasableOrders',
    icon: <VpnKeyIcon sx={{ fontSize: 22, color: '#f5a623' }} />,
    accentColor: '#f5a623',
  },
  {
    key: HOLD_ALLOCATION_BUCKETS.LOCKED,
    countKey: 'lockedOrders',
    icon: <LockIcon sx={{ fontSize: 22, color: '#cc3f0c' }} />,
    accentColor: '#cc3f0c',
  },
  {
    key: HOLD_ALLOCATION_BUCKETS.READY_TO_ALLOCATE,
    countKey: 'readyToAllocateOrders',
    icon: <RocketLaunchIcon sx={{ fontSize: 22, color: '#2db47d' }} />,
    accentColor: '#2db47d',
  },
]

function BucketCardsV2({ dashboard, selectedBucket, onBucketClick }) {
  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {BUCKET_CONFIG.map(({ key, countKey, icon, accentColor }) => {
        const isSelected = selectedBucket === key
        const count = dashboard ? dashboard[countKey] : '-'
        return (
          <Box
            key={key}
            onClick={() => onBucketClick(key)}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: isSelected ? `2px solid ${accentColor}` : '2px solid transparent',
              boxShadow: isSelected
                ? `0 2px 12px ${accentColor}22`
                : '0 1px 4px rgba(0,0,0,0.06)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                boxShadow: `0 2px 12px ${accentColor}22`,
                borderColor: accentColor,
              },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '2.2rem',
                  fontWeight: 700,
                  fontFamily: 'SanomatMedium, serif',
                  color: '#1a1a1a',
                  lineHeight: 1,
                  mb: 0.5,
                }}
              >
                {count}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  fontFamily: 'AtlasGroteskRegular, sans-serif',
                  color: '#666666',
                  lineHeight: 1.2,
                }}
              >
                {BUCKET_LABELS[key]}
              </Typography>
            </Box>
            {icon}
          </Box>
        )
      })}
    </Box>
  )
}

BucketCardsV2.propTypes = {
  dashboard: PropTypes.shape({
    allOrders: PropTypes.number,
    releasableOrders: PropTypes.number,
    readyToAllocateOrders: PropTypes.number,
    lockedOrders: PropTypes.number,
    // Awaiting-stock orders are returned by the API but are not
    // currently rendered as a bucket card — see HOLD_ALLOCATION_BUCKETS
    // in hold-allocation-constants.js for the rationale.
    awaitingStockOrders: PropTypes.number,
    // Per-bucket chip + hold-code breakdown tree. Consumed by
    // HoldFilterBarV2 via the selectBucketBreakdownForSelected
    // selector; not read directly here.
    bucketBreakdowns: PropTypes.objectOf(
      PropTypes.shape({
        orderCount: PropTypes.number,
        primaryChips: PropTypes.arrayOf(PropTypes.shape({})),
        secondaryChips: PropTypes.arrayOf(PropTypes.shape({})),
        additionalHolds: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
  }),
  selectedBucket: PropTypes.string.isRequired,
  onBucketClick: PropTypes.func.isRequired,
}

export default BucketCardsV2
